import { createElement } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { saveUserData } from "@/stores/buildings/repository";
import { useStores } from "@/stores";
import { useToast } from "@/hooks/use-toast";
import { ToastAction, type ToastActionElement } from "@/components/ui/toast";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { WIDGET_QUERY_KEY_PREFIX } from "./useBuildingWidgetData";
import { ATTRIBUTE_HISTORY_QUERY_KEY } from "./useAttributeHistory";
import { BUILDING_DATA_HISTORY_QUERY_KEY } from "./useBuildingDataHistory";

interface SaveFieldInput {
  attributeKey: string;
  attributeValue: string | null;
  /**
   * Value to append when the user chooses Undo. A blank string removes the
   * effective override; SQL NULL records a rejection. Omit for silent writes.
   */
  previousValue?: string | null;
  /** Show a success toast with an Undo action. */
  offerUndo?: boolean;
  /** Optional action-specific toast text. */
  successMessage?: string;
  /**
   * Suppress the per-failure error toast. Bulk callers fan this mutation out
   * over many fields and report one aggregate result themselves — without this
   * N failures would stack N destructive toasts on top of that summary.
   */
  silentError?: boolean;
}

/**
 * Writes an attribute value to `data.user_data` via the existing `saveUserData`
 * RPC wrapper. Resolves `orgId`, `buildingId`, and `editedBy` from the MST root
 * store (same pattern as `BuildingStore.saveUserAttributes`) so callers only
 * have to pass the attribute key + value.
 *
 * Invalidates the widget query on success. Toasts on error.
 */
export function useSaveFieldValue(buildingId: string | undefined, orgId: string | undefined) {
  const queryClient = useQueryClient();
  const { userStore } = useStores();
  const { toast } = useToast();
  const { t } = useLanguage();
  const tw = t.buildings.buildingDataWidget;

  const resolveContext = () => {
    const userProfileId = userStore.userProfile?.id;
    // Only the end-user-facing message is translated. The other two errors
    // indicate app-level bugs (missing org ctx / bad id) and stay English
    // so they remain greppable in log aggregators.
    if (!userProfileId) throw new Error(tw.missingUserProfile);
    if (!orgId) throw new Error("Organisation context missing");
    if (!buildingId) throw new Error("Building id missing");
    const buildingIdNum = parseInt(buildingId, 10);
    if (Number.isNaN(buildingIdNum)) throw new Error("Building id is not numeric");

    return { userProfileId, buildingIdNum, resolvedOrgId: orgId };
  };

  const writeValue = async (attributeKey: string, attributeValue: string | null) => {
    const { userProfileId, buildingIdNum, resolvedOrgId } = resolveContext();

    await saveUserData({
      orgId: resolvedOrgId,
      buildingId: buildingIdNum,
      attributeKey,
      attributeValue,
      editedBy: userProfileId,
    });
  };

  const invalidateBuildingData = (attributeKey: string) => {
    // Prefix invalidation hits both ["en"] and ["et"] entries — otherwise
    // saving in one locale leaves the other locale's cache stale until
    // staleTime expires (WHA-3xxx: user saw "Select an option" for ~5 min
    // after switching language post-save).
    queryClient.invalidateQueries({
      queryKey: [WIDGET_QUERY_KEY_PREFIX, buildingId, orgId],
    });
    // The Building dashboard reads from a separate query; invalidate it
    // too so the header updates without a manual refresh (WHA-3141).
    queryClient.invalidateQueries({
      queryKey: ["building-header-attributes", buildingId, orgId],
    });
    queryClient.invalidateQueries({
      queryKey: [ATTRIBUTE_HISTORY_QUERY_KEY, buildingId, orgId, attributeKey],
    });
    // The building-wide timeline is a second reader of the same append-only
    // table — without this the history modal keeps showing the pre-save rows.
    queryClient.invalidateQueries({
      queryKey: [BUILDING_DATA_HISTORY_QUERY_KEY, buildingId, orgId],
    });
  };

  return useMutation({
    mutationFn: async ({ attributeKey, attributeValue }: SaveFieldInput) => {
      await writeValue(attributeKey, attributeValue);
    },
    onSuccess: (_data, variables) => {
      invalidateBuildingData(variables.attributeKey);
      if (!variables.offerUndo || variables.previousValue === undefined) return;

      const undo = async () => {
        try {
          await writeValue(variables.attributeKey, variables.previousValue);
          invalidateBuildingData(variables.attributeKey);
          toast({ description: tw.actionUndone });
        } catch (error) {
          const message = error instanceof Error ? error.message : "Unknown error";
          toast({ variant: "destructive", title: tw.saveError, description: message });
        }
      };

      toast({
        description: variables.successMessage ?? tw.actionSaved,
        action: createElement(
          ToastAction,
          { altText: tw.undoAction, onClick: () => void undo() },
          tw.undoAction
        ) as unknown as ToastActionElement,
      });
    },
    onError: (error: unknown, variables: SaveFieldInput) => {
      if (variables.silentError) return;
      const message = error instanceof Error ? error.message : "Unknown error";
      toast({
        variant: "destructive",
        title: tw.saveError,
        description: message,
      });
    },
  });
}
